import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Panel, SectionTitle } from '../../../../components';
import { previewUrl } from '../../preview-url';
import { resolveRouteText, setRouteImage, setSiteImage, titleTemplateFor } from '../../seo-document';
import { firstErrorMessage, issuesFor } from '../../issues-by-field';
import type { ImageRef, SiteSeoDocument, SiteSeoIssue, SiteSeoRoute } from '../../seo-types';
import { validImageUrl } from '../../seo-validation';
import type { LiveMetaEntry } from '../../site-seo-state';
import { applyTitleTemplate, sampleName } from '../../title-template';
import { ImagePickerSheet, isSmallShareImage, type ImagePickerTab } from '../ImagePickerSheet';
import {
  Actions,
  Body,
  Card,
  CardDescription,
  CardHost,
  CardImage,
  CardLabel,
  CardText,
  CardTitle,
  Dimensions,
  Empty,
  ErrorText,
  Head,
  ImageMeta,
  ImageRow,
  ImageThumb,
  Note,
  WarnText,
} from './SharePanel.styles';

export type ShareImageFieldProps = {
  /** The image shares show (the owner's own, else the one it inherits). */
  shown?: ImageRef;
  /** `shown` is the site default, not this level's own image. */
  inherited: boolean;
  /** Offer "Use site default" (route level): enabled while the route has its own image. */
  allowSiteDefault: boolean;
  tenantId?: number;
  brandName: string;
  onPick: (image: ImageRef | undefined) => void;
};

/** The effective share image with its size and source, Upload / From library (/ Use site default), and the picker. */
export const ShareImageField = ({
  shown,
  inherited,
  allowSiteDefault,
  tenantId,
  brandName,
  onPick,
}: ShareImageFieldProps): React.ReactElement => {
  const { t } = useTranslation();
  const [picker, setPicker] = React.useState<ImagePickerTab | null>(null);
  const pick = (image: ImageRef | undefined): void => {
    setPicker(null);
    onPick(image);
  };

  return (
    <>
      <ImageRow data-share-image>
        <ImageThumb>
          {shown && validImageUrl(shown.url) ? (
            <img src={shown.url} alt="" />
          ) : (
            <Empty text="No image yet" tag="span" />
          )}
        </ImageThumb>
        <ImageMeta>
          {shown && shown.width > 0 && shown.height > 0 ? (
            <Dimensions text={`${shown.width}×${shown.height}`} raw tag="span" />
          ) : null}
          {shown && inherited ? <Note text="From site defaults" tag="span" /> : null}
          {shown && isSmallShareImage(shown) ? (
            <WarnText text="Smaller than 1200×630: it may look soft when shared" tag="span" />
          ) : null}
        </ImageMeta>
      </ImageRow>
      <Actions>
        <Button label={t('Upload')} variant="outline" icon="image" onClick={() => setPicker('upload')} />
        <Button label={t('From library')} variant="outline" onClick={() => setPicker('library')} />
        {allowSiteDefault ? (
          <Button
            label={t('Use site default')}
            variant="text"
            disabled={inherited || !shown}
            onClick={() => {
              if (inherited || !shown) return;
              onPick(undefined);
            }}
          />
        ) : null}
      </Actions>
      <ImagePickerSheet
        open={picker !== null}
        initialTab={picker ?? 'upload'}
        tenantId={tenantId}
        brandName={brandName}
        current={inherited ? undefined : shown}
        allowSiteDefault={allowSiteDefault}
        onPick={pick}
        onClose={() => setPicker(null)}
      />
    </>
  );
};

export type SharePanelProps = {
  draft: SiteSeoDocument;
  /** null = Site defaults (the site's own image, no preview). */
  route: SiteSeoRoute | null;
  /** The route's on-demand live meta (null = not asked for): the platform's title and description. */
  liveMeta: LiveMetaEntry | null;
  language: string;
  defaultLanguage: string;
  /** The site's address, e.g. `https://brand.com`. */
  siteUrl: string;
  tenantId?: number;
  brandName: string;
  /** A footer wildcard: meta is not set on a group of paths. */
  disabled: boolean;
  /** The server's issues; the route's share image shows its first error as is. */
  issues: SiteSeoIssue[];
  onChange: (update: (draft: SiteSeoDocument) => SiteSeoDocument) => void;
};

/**
 * Social share for one route: the image shares show (the route's own, else the site default, else none) with Upload,
 * From library and "Use site default", and the share card as Facebook and X draw it, with the title and description of
 * Search result (title through the template, a dynamic page with a sample game name). A footer wildcard is inert.
 */
export const SharePanel = ({
  draft,
  route,
  liveMeta,
  language,
  defaultLanguage,
  siteUrl,
  tenantId,
  brandName,
  disabled,
  issues,
  onChange,
}: SharePanelProps): React.ReactElement => {
  const { t } = useTranslation();
  const own = route ? route.share_image : draft.site.share_image;
  const site = draft.site.share_image;
  const shown = own ?? (route ? site : undefined);
  const imageError = firstErrorMessage(issuesFor(issues, route?.path ?? null, 'share_image'));

  const pickImage = (image: ImageRef | undefined): void => {
    if (disabled) return;
    if (route) {
      const { path } = route;
      onChange((d) => setRouteImage(d, path, image));
    } else onChange((d) => setSiteImage(d, image));
  };

  // The same title and description as Search result's Google preview (keep the two in step).
  const card = ((): { host: string; title: string; description: string } | null => {
    if (!route) return null;
    const { path } = route;
    const live = liveMeta?.status === 'ready' ? liveMeta.value : null;
    const sample = (text: string): string => (route.match === 'pattern' ? sampleName(text, t('Game name')) : text);
    const title = resolveRouteText(draft, live, path, 'title', language, defaultLanguage);
    const description = resolveRouteText(draft, live, path, 'description', language, defaultLanguage);
    return {
      host: previewUrl(siteUrl, path, language, defaultLanguage).replace(/^https?:\/\//, ''),
      title: title.value
        ? sample(
            title.source === 'platform'
              ? title.value
              : applyTitleTemplate(titleTemplateFor(draft, title.source, language, defaultLanguage), title.value),
          )
        : '',
      description: sample(description.value),
    };
  })();

  return (
    <Panel>
      <Head>
        <SectionTitle title="Social share" />
        {!disabled && route ? <Note text="The title and description come from Search result." tag="span" /> : null}
      </Head>
      <Body>
        {disabled ? (
          <Note text="Meta is set on the site's pages, not on a group of paths." tag="paragraph" />
        ) : (
          <>
            <ShareImageField
              shown={shown}
              inherited={own === undefined && shown !== undefined}
              allowSiteDefault={route !== null}
              tenantId={tenantId}
              brandName={brandName}
              onPick={pickImage}
            />
            {imageError ? <ErrorText text={imageError} raw tag="span" /> : null}
            {card ? (
              <Card aria-label={t('Share preview')}>
                <CardLabel text="Facebook / X preview" tag="span" />
                <CardImage>
                  {shown && validImageUrl(shown.url) ? (
                    <img src={shown.url} alt="" />
                  ) : (
                    <Empty text="No image yet" tag="span" />
                  )}
                </CardImage>
                <CardText>
                  <CardHost text={card.host} raw tag="span" />
                  {card.title ? (
                    <CardTitle text={card.title} raw tag="span" />
                  ) : (
                    <Empty text="No title yet" tag="span" />
                  )}
                  {card.description ? <CardDescription text={card.description} raw tag="span" /> : null}
                </CardText>
              </Card>
            ) : null}
          </>
        )}
      </Body>
    </Panel>
  );
};

export default SharePanel;
